"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getModelConfig } from "@/client/store/modelStore";
import type { ModelConfigEntry } from "@/client/config/models.config";
import type { SchemaGenerationPayload } from "@/client/types/generation";
import { isRecord } from "@/client/utils/typeGuards";
import { getAdapter } from "@/client/adapters/registry";
import type { AdapterContext } from "@/client/adapters/types";
import { extractRequestId, isSubmittedGeneration } from "@/client/utils/generationUtils";
import { buildPayloadFromConfig } from "@/features/workspace/modelFormAdapter";
import { isBatchSupportedModel } from "../helpers/modelHelpers";
import type { BatchTaskItem } from "../types";
import { useBatchTasks } from "../context";
import { useLanguageStore } from "@/client/store/languageStore";
import { getBatchResultCsvFilename } from "../utils/filenameLocales";

const BATCH_SIZE = 3;
const BATCH_DELAY_MS = 500;

export interface BatchSubmissionOptions {
  taskIds?: string[];
}

export interface BatchSubmissionResult {
  totalTargets: number;
  attempted: number;
  success: number;
  failed: number;
  skipped: number;
  csvContent: string | null;
  csvFilename: string | null;
  csvRowCount: number;
  aborted?: boolean;
  error?: string;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const applyModelDefaultsToPayload = (
  sourcePayload: SchemaGenerationPayload,
  modelConfig?: ModelConfigEntry | null
): SchemaGenerationPayload => {
  const defaults = modelConfig?.defaults;
  if (!isRecord(defaults) || Array.isArray(defaults)) {
    return sourcePayload;
  }

  const currentApi = isRecord(sourcePayload.api) ? { ...sourcePayload.api } : {};
  let mutated = false;

  for (const [key, value] of Object.entries(defaults)) {
    if (key === "headers" && isRecord(value) && !Array.isArray(value)) {
      const existingHeaders: Record<string, string> = isRecord(currentApi.headers)
        ? { ...(currentApi.headers as Record<string, string>) }
        : {};
      let headerMutated = false;
      for (const [headerKey, headerValue] of Object.entries(value)) {
        if (existingHeaders[headerKey] !== undefined) {
          continue;
        }
        if (headerValue == null) {
          continue;
        }
        existingHeaders[headerKey] =
          typeof headerValue === "string" ? headerValue : String(headerValue);
        headerMutated = true;
      }
      if (headerMutated) {
        currentApi.headers = existingHeaders;
        mutated = true;
      }
      continue;
    }

    if ((currentApi as Record<string, unknown>)[key] === undefined) {
      (currentApi as Record<string, unknown>)[key] = value as unknown;
      mutated = true;
    }
  }

  if (!mutated) {
    return sourcePayload;
  }

  return {
    ...sourcePayload,
    api: currentApi as SchemaGenerationPayload["api"],
  };
};

interface PreparedTask {
  task: BatchTaskItem;
  config: ModelConfigEntry;
  values: unknown;
}

interface CsvRow {
  prompt: string;
  modelKey: string;
  taskId: string;
  csvIndex?: number;
  csvLine?: number;
}

const escapeCsvField = (value: string): string => {
  const safe = value.replace(/"/g, '""');
  if (/[",\n\r]/.test(safe)) {
    return `"${safe}"`;
  }
  return safe;
};

const buildCsvContent = (rows: CsvRow[]): string => {
  const header = ["prompt", "model_key", "task_id", "csv_row"];
  const lines: string[] = [header.join(",")];
  rows.forEach((row) => {
    const position = row.csvIndex ?? row.csvLine;
    const csvValue = position != null ? String(position) : "";
    lines.push(
      [
        escapeCsvField(row.prompt),
        escapeCsvField(row.modelKey),
        escapeCsvField(row.taskId),
        escapeCsvField(csvValue),
      ].join(",")
    );
  });
  return `${lines.join("\r\n")}\r\n`;
};

export const useBatchSubmission = () => {
  const { tasks, updateTasksByIds } = useBatchTasks();
  const language = useLanguageStore((state) => state.language);
  const tasksRef = useRef<BatchTaskItem[]>(tasks);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  const submitTasks = useCallback(
    async (options: BatchSubmissionOptions = {}): Promise<BatchSubmissionResult> => {
      if (submittingRef.current) {
        return { totalTargets: 0, attempted: 0, success: 0, failed: 0, skipped: 0, csvContent: null, csvFilename: null, csvRowCount: 0, aborted: true };
      }

      submittingRef.current = true;
      setSubmitting(true);

      try {
        const allTasks = tasksRef.current;
        const targetSet = options.taskIds ? new Set(options.taskIds) : null;
        const targetTasks = targetSet
          ? allTasks.filter((task) => targetSet.has(task.id))
          : allTasks.slice();

        const totalTargets = targetTasks.length;
        if (totalTargets === 0) {
          return {
            totalTargets: 0,
            attempted: 0,
            success: 0,
            failed: 0,
            skipped: 0,
            csvContent: null,
            csvFilename: null,
            csvRowCount: 0,
          };
        }

        const prepared: PreparedTask[] = [];
        let skipped = 0;

        for (const task of targetTasks) {
          if (!task.modelKey) {
            skipped += 1;
            updateTasksByIds([task.id], (prev) => ({
              ...prev,
              status: "failed",
              error: "未选择生成方案。",
              taskId: null,
              submittedAt: null,
            }));
            continue;
          }

          const config = getModelConfig(task.modelKey);
          if (!config) {
            skipped += 1;
            updateTasksByIds([task.id], (prev) => ({
              ...prev,
              status: "failed",
              error: "未找到模型配置，请检查模型权限。",
              taskId: null,
              submittedAt: null,
            }));
            continue;
          }

          if (!isBatchSupportedModel(config)) {
            skipped += 1;
            updateTasksByIds([task.id], (prev) => ({
              ...prev,
              status: "failed",
              error: "当前模型不支持批量提交。",
              taskId: null,
              submittedAt: null,
            }));
            continue;
          }

          const values = task.formValues?.[task.modelKey];
          if (values === undefined || values === null) {
            skipped += 1;
            updateTasksByIds([task.id], (prev) => ({
              ...prev,
              status: "failed",
              error: "缺少模型参数，请先在右侧完成配置。",
              taskId: null,
              submittedAt: null,
            }));
            continue;
          }

          prepared.push({
            task,
            config,
            values,
          });
        }

        const attempted = prepared.length;
        if (attempted === 0) {
          return {
            totalTargets,
            attempted,
            success: 0,
            failed: 0,
            skipped,
            csvContent: null,
            csvFilename: null,
            csvRowCount: 0,
          };
        }

        let success = 0;
        let failed = 0;
        const csvRows: CsvRow[] = [];

        for (let index = 0; index < prepared.length; index += BATCH_SIZE) {
          const batch = prepared.slice(index, index + BATCH_SIZE);
          const batchIds = batch.map((item) => item.task.id);

          updateTasksByIds(batchIds, (prev) => ({
            ...prev,
            status: "pending",
            error: null,
          }));

          await Promise.all(
            batch.map(async (entry) => {
              const { task, config, values } = entry;
              try {
                const payload = await buildPayloadFromConfig(config, values);
                const normalizedPayload = applyModelDefaultsToPayload(payload, config);
                const adapter = getAdapter(config.modelKey, config.defaultTaskType);
                if (!adapter) {
                  throw new Error("未找到模型适配器，无法提交任务。");
                }
                const context: AdapterContext = {
                  provider: config.modelKey,
                  taskType: config.defaultTaskType,
                  model: task.modelKey as string,
                  modelConfig: config,
                };
                const result = await adapter.generate(normalizedPayload, context);
                const requestId = extractRequestId(result);
                const submitted = isSubmittedGeneration(result);
                const status = submitted ? "queued" : "success";
                const now = Date.now();

                updateTasksByIds([task.id], (prev) => ({
                  ...prev,
                  status,
                  error: null,
                  taskId: requestId ?? prev.taskId ?? null,
                  submittedAt: now,
                }));

                if (requestId) {
                  const csvIndexValue =
                    typeof task.meta?.csvIndex === "number"
                      ? task.meta.csvIndex
                      : csvRows.length + 1;
                  csvRows.push({
                    prompt: task.prompt,
                    modelKey: task.modelKey as string,
                    taskId: requestId,
                    csvIndex: csvIndexValue,
                    csvLine: task.meta?.csvLine,
                  });
                }

                success += 1;
              } catch (error) {
                const message =
                  error instanceof Error && error.message
                    ? error.message
                    : "提交任务失败，请稍后重试。";
                updateTasksByIds([task.id], (prev) => ({
                  ...prev,
                  status: "failed",
                  error: message,
                  taskId: prev.taskId ?? null,
                  submittedAt: prev.submittedAt ?? null,
                }));
                failed += 1;
              }
            })
          );

          if (index + BATCH_SIZE < prepared.length) {
            await delay(BATCH_DELAY_MS);
          }
        }

        const csvRowCount = csvRows.length;
        let csvContent: string | null = null;
        let csvFilename: string | null = null;
        if (csvRowCount > 0) {
          csvContent = buildCsvContent(csvRows);
          csvFilename = getBatchResultCsvFilename(language);
        }

        return {
          totalTargets,
          attempted,
          success,
          failed,
          skipped,
          csvContent,
          csvFilename,
          csvRowCount,
        };
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "提交任务过程中出现异常。";
        return {
          totalTargets: 0,
          attempted: 0,
          success: 0,
          failed: 0,
          skipped: 0,
          csvContent: null,
          csvFilename: null,
          csvRowCount: 0,
          error: message,
        };
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [updateTasksByIds, language]
  );

  return {
    submitTasks,
    submitting,
  };
};
